<template>
  {{ formattedDeliveryDate }}
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api/src/standards/DateTime'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './DeliveryDate.translations'

const i18n = useI18n()

const {
  promisedMinDeliveryDate,
  promisedMaxDeliveryDate,
  estimatedDeliveryDate,
} = defineProps<{
  promisedMinDeliveryDate: DateTime
  promisedMaxDeliveryDate: DateTime
  estimatedDeliveryDate: DateTime | null
}>()

const isToday = (date: Date) => {
  const today = new Date()

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

const formattedPromisedDeliveryDateRange = computed(() => {
  const minDate = new Date(promisedMinDeliveryDate)
  const maxDate = new Date(promisedMaxDeliveryDate)

  return formatDateRange(minDate, maxDate)
})

const formattedDeliveryDate = computed(() => {
  if (estimatedDeliveryDate) {
    const date = new Date(estimatedDeliveryDate)

    return formatSingleDate(date)
  }

  return formattedPromisedDeliveryDateRange.value
})

function formatSingleDate(date: Date) {
  const weekday = isToday(date)
    ? i18n(translations.today)
    : i18n.date(date, { weekday: 'long' })
  const formattedMonth = i18n.date(date, { month: 'short' })
  const formattedDay = i18n.date(date, { day: 'numeric' })

  return i18n(translations.singleDeliveryDate, {
    weekday,
    month: formattedMonth,
    day: formattedDay,
  })
}

function formatDateRange(minDate: Date, maxDate: Date) {
  const isSameMonth =
    minDate.getMonth() === maxDate.getMonth() &&
    minDate.getFullYear() === maxDate.getFullYear()

  if (isSameMonth) {
    const month = i18n.date(minDate, { month: 'short' })

    return i18n(translations.sameMonthDeliveryDateRange, {
      month,
      day1: minDate.getDate(),
      day2: maxDate.getDate(),
    })
  }

  const minFormattedMonth = i18n.date(minDate, { month: 'short' })
  const minFormattedDay = i18n.date(minDate, { day: 'numeric' })
  const maxFormattedMonth = i18n.date(maxDate, { month: 'short' })
  const maxFormattedDay = i18n.date(maxDate, { day: 'numeric' })

  return i18n(translations.splitMonthDeliveryDateRange, {
    month1: minFormattedMonth,
    day1: minFormattedDay,
    month2: maxFormattedMonth,
    day2: maxFormattedDay,
  })
}
</script>
